import { ApiError, type FileResponse } from "@/lib/api";
import type { AuthedBlobRequester, AuthedRequester } from "@/lib/brokerAuth";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";

// BRK-S468 — a bound quote's binder and policy (PMS-S560), read for the quote
// popup's bound summary: the numbers a broker quotes back to the insured, the
// period, the total, and the carrier's papers to download. Each reader takes
// only what PMS publishes on its record (held to the pin) and reads tolerantly:
// a record it can't name is none, never a throw (NN #7). Built to be reused by
// BRK-S215's binder and policy pages.

/** A paper filed on a binder or a policy, as the broker may download it. */
export interface BoundDocument {
  id: string;
  /** The account-document type: `binder`, `policy`, `endorsement`, … */
  type: string;
  filename: string;
  /** A newer version of the same type exists. */
  superseded: boolean;
}

export interface BoundBinder {
  id: string;
  /** BND-{code}-{seq}. */
  binderNumber: string;
  /** ISO `YYYY-MM-DD`. */
  effectiveDate: string;
  binderExpiresOn: string;
  /** The policy it converted to, by number. */
  policyNumber: string | null;
  totalCost: string;
  /** Newest first. */
  documents: BoundDocument[];
}

export interface BoundPolicy {
  id: string;
  /** POL-{code}-{seq}. */
  policyNumber: string;
  binderNumber: string | null;
  /** ISO `YYYY-MM-DD`. */
  effectiveDate: string;
  expirationDate: string;
  /** The current version's total; null when its version can't be read. */
  totalCost: string | null;
  /** Newest first. */
  documents: BoundDocument[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const text = (value: unknown): value is string => typeof value === "string";

/** The papers it can download: a row with no id, type or name is skipped. */
function readDocuments(value: unknown): BoundDocument[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((row): BoundDocument[] =>
    isRecord(row) && text(row.id) && text(row.type) && text(row.filename)
      ? [
          {
            id: row.id,
            type: row.type,
            filename: row.filename,
            superseded: row.superseded === true,
          },
        ]
      : [],
  );
}

/** GET /api/broker/binders/{id}'s body, or null when it can't be read. */
export function readBinder(body: unknown): BoundBinder | null {
  if (!isRecord(body)) return null;
  const { id, binderNumber, effectiveDate, binderExpiresOn, totalCost } = body;
  if (
    !text(id) ||
    !text(binderNumber) ||
    !text(effectiveDate) ||
    !text(binderExpiresOn) ||
    !text(totalCost)
  )
    return null;
  return {
    id,
    binderNumber,
    effectiveDate,
    binderExpiresOn,
    policyNumber: text(body.policyNumber) ? body.policyNumber : null,
    totalCost,
    documents: readDocuments(body.documents),
  };
}

/** GET /api/broker/policies/{id}'s body, or null when it can't be read. */
export function readPolicy(body: unknown): BoundPolicy | null {
  if (!isRecord(body)) return null;
  const { id, policyNumber, effectiveDate, expirationDate } = body;
  if (
    !text(id) ||
    !text(policyNumber) ||
    !text(effectiveDate) ||
    !text(expirationDate)
  )
    return null;
  const version = body.currentVersion;
  return {
    id,
    policyNumber,
    binderNumber: text(body.binderNumber) ? body.binderNumber : null,
    effectiveDate,
    expirationDate,
    totalCost:
      isRecord(version) && text(version.totalCost) ? version.totalCost : null,
    documents: readDocuments(body.documents),
  };
}

/** GET /api/broker/binders/{id} (policy:view, own-scoped). */
export async function getBinder(
  request: AuthedRequester,
  id: string,
): Promise<BoundBinder> {
  const binder = readBinder(
    await request<unknown>(`/api/broker/binders/${encodeURIComponent(id)}`),
  );
  if (!binder)
    throw new ApiError(200, "Signal answered, but not with the binder.");
  return binder;
}

/** GET /api/broker/policies/{id} (policy:view, own-scoped). */
export async function getPolicy(
  request: AuthedRequester,
  id: string,
): Promise<BoundPolicy> {
  const policy = readPolicy(
    await request<unknown>(`/api/broker/policies/${encodeURIComponent(id)}`),
  );
  if (!policy)
    throw new ApiError(200, "Signal answered, but not with the policy.");
  return policy;
}

/** A bound quote's records: the quote's own ids, else its bind request's. */
export function boundIds(quote: BrokerQuote): {
  binderId: string | null;
  policyId: string | null;
} {
  return {
    binderId: quote.binderId ?? quote.bindRequest?.binderId ?? null,
    policyId: quote.policyId ?? quote.bindRequest?.policyId ?? null,
  };
}

export const BOUND_DOWNLOAD_LABELS = {
  binder: "Download Binder",
  policy: "Download Policy",
} as const;

/** A paper to offer, and the record whose route serves it. */
export interface BoundDownload {
  label: (typeof BOUND_DOWNLOAD_LABELS)[keyof typeof BOUND_DOWNLOAD_LABELS];
  route: "binder" | "policy";
  recordId: string;
  document: BoundDocument;
}

/** What the bound summary shows. Null where nothing says it yet. */
export interface BoundFacts {
  /** ISO `YYYY-MM-DD`. */
  periodFrom: string | null;
  periodTo: string | null;
  binderNumber: string | null;
  policyNumber: string | null;
  totalCost: string | null;
  downloads: BoundDownload[];
}

/** The newest current paper of a type: documents come newest first. */
function current(
  documents: readonly BoundDocument[],
  type: string,
): BoundDocument | null {
  return documents.find((d) => d.type === type && !d.superseded) ?? null;
}

/**
 * What a bound quote shows, from the most settled record: the policy once
 * issued (it owns its terms), else the binder, else the quote as it was bound.
 * The policy PDF downloads from the policy, or from the binder when it
 * arrived there first.
 */
export function boundFacts(
  quote: BrokerQuote,
  binder: BoundBinder | null,
  policy: BoundPolicy | null,
): BoundFacts {
  const downloads: BoundDownload[] = [];
  const binderPaper = binder && current(binder.documents, "binder");
  if (binder && binderPaper)
    downloads.push({
      label: BOUND_DOWNLOAD_LABELS.binder,
      route: "binder",
      recordId: binder.id,
      document: binderPaper,
    });
  const policyPaper = policy && current(policy.documents, "policy");
  const earlyPolicy = binder && current(binder.documents, "policy");
  if (policy && policyPaper)
    downloads.push({
      label: BOUND_DOWNLOAD_LABELS.policy,
      route: "policy",
      recordId: policy.id,
      document: policyPaper,
    });
  else if (binder && earlyPolicy)
    downloads.push({
      label: BOUND_DOWNLOAD_LABELS.policy,
      route: "binder",
      recordId: binder.id,
      document: earlyPolicy,
    });
  return {
    periodFrom:
      policy?.effectiveDate ??
      binder?.effectiveDate ??
      quote.policyEffectiveDate,
    periodTo: policy?.expirationDate ?? quote.policyExpirationDate,
    binderNumber: binder?.binderNumber ?? policy?.binderNumber ?? null,
    policyNumber: policy?.policyNumber ?? binder?.policyNumber ?? null,
    totalCost:
      policy?.totalCost ??
      binder?.totalCost ??
      quote.terms?.totals.totalCost ??
      quote.totalCost,
    downloads,
  };
}

/** A paper's bytes, through its own record's route (PMS-S560): 404 for any
 *  miss, 502 when the store didn't answer. */
export function downloadBound(
  fetchBlob: AuthedBlobRequester,
  download: BoundDownload,
): Promise<FileResponse> {
  const base =
    download.route === "binder"
      ? "/api/broker/binders"
      : "/api/broker/policies";
  return fetchBlob(
    `${base}/${encodeURIComponent(download.recordId)}/documents/${encodeURIComponent(download.document.id)}/download`,
  );
}
